import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Ear, Lock, Envelope, User, Building, Phone, MapPin, Eye, EyeSlash, ShieldCheck } from '@phosphor-icons/react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register, verify2fa, resend2fa } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [twoFa, setTwoFa] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [showLoginPass, setShowLoginPass] = useState(false);
  const [showRegPass, setShowRegPass] = useState(false);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register form state
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [regCompany, setRegCompany] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regAddress, setRegAddress] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const result = await login(loginEmail, loginPassword);
    setIsLoading(false);
    
    if (result.requires_2fa) {
      setTwoFa({ temp_token: result.temp_token, email_hint: result.email_hint });
      setOtpCode('');
      return;
    }
    if (result.success) {
      redirectByRole(result.user?.role);
    }
  };

  const redirectByRole = (role) => {
    if (role === 'client') navigate('/client');
    else navigate('/dashboard');
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    if (otpCode.trim().length !== 6) return;
    setIsLoading(true);
    const result = await verify2fa(twoFa.temp_token, otpCode.trim());
    setIsLoading(false);
    if (result.success) redirectByRole(result.user?.role);
    else if (result.status === 429 || (typeof result.error === 'string' && result.error.includes('reconnectez'))) setTwoFa(null);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const result = await register({
      email: regEmail,
      password: regPassword,
      name: regName,
      company_name: regCompany,
      phone: regPhone,
      address: regAddress,
      role: 'client'
    });
    setIsLoading(false);
    
    if (result.success) {
      navigate('/client');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative noise-bg bg-zinc-900">
        <img 
          src="https://images.unsplash.com/photo-1639195612006-b78f028c552a?auto=format&fit=crop&w=1600&q=80"
          alt="CNC Machine"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Ear size={40} weight="duotone" />
            <span className="font-heading text-2xl font-bold tracking-tight">HAMAC SOLUTIONS</span>
          </div>
          <p className="text-zinc-300 text-lg max-w-md">
            Fabrication d'embouts auditifs sur mesure. 
            Précision et qualité au service de votre audition.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-zinc-100">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <Ear size={32} weight="duotone" className="text-zinc-900" />
            <span className="font-heading text-xl font-bold tracking-tight">HAMAC SOLUTIONS</span>
          </div>

          <div className="bg-white border border-zinc-200 p-8" data-testid="login-container">
            {twoFa ? (
              <form onSubmit={handleVerify2fa} className="space-y-5" data-testid="twofa-form">
                <div className="flex items-center gap-3">
                  <ShieldCheck size={28} weight="duotone" className="text-zinc-900" />
                  <div>
                    <h2 className="font-heading font-semibold text-base">Double authentification</h2>
                    <p className="text-xs text-zinc-500">Un code à 6 chiffres a été envoyé à <span className="font-medium text-zinc-700">{twoFa.email_hint}</span>. Valable 10 minutes.</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="otp-code" className="label-overline">Code reçu par e-mail</Label>
                  <Input
                    id="otp-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="rounded-sm text-center text-2xl tracking-[0.5em] font-mono h-14"
                    autoFocus
                    required
                    data-testid="twofa-code-input"
                  />
                </div>
                <Button type="submit" className="w-full rounded-sm bg-zinc-900 hover:bg-zinc-800" disabled={isLoading || otpCode.length !== 6} data-testid="twofa-submit-btn">
                  {isLoading ? <span className="spinner" /> : 'Valider le code'}
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <button type="button" onClick={() => resend2fa(twoFa.temp_token)} className="text-zinc-500 hover:text-zinc-900 underline underline-offset-4" data-testid="twofa-resend-btn">
                    Renvoyer le code
                  </button>
                  <button type="button" onClick={() => setTwoFa(null)} className="text-zinc-500 hover:text-zinc-900 underline underline-offset-4" data-testid="twofa-back-btn">
                    Retour
                  </button>
                </div>
              </form>
            ) : (
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 rounded-sm">
                <TabsTrigger value="login" className="rounded-sm" data-testid="login-tab">
                  Connexion
                </TabsTrigger>
                <TabsTrigger value="register" className="rounded-sm" data-testid="register-tab">
                  Créer un compte
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="label-overline">Email</Label>
                    <div className="relative">
                      <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="votre@email.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="pl-10 rounded-sm"
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="label-overline">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                      <Input
                        id="login-password"
                        type={showLoginPass ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="pl-10 pr-10 rounded-sm"
                        required
                        data-testid="login-password-input"
                      />
                      <button type="button" onClick={() => setShowLoginPass(!showLoginPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors" data-testid="toggle-login-password">
                        {showLoginPass ? <EyeSlash size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full rounded-sm bg-zinc-900 hover:bg-zinc-800"
                    disabled={isLoading}
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? <span className="spinner" /> : 'Se connecter'}
                  </Button>
                </form>

                <div className="mt-4 text-center">
                  <button type="button" onClick={() => navigate('/forgot-password')} className="text-sm text-zinc-500 hover:text-zinc-900 underline underline-offset-4" data-testid="forgot-password-link">
                    Mot de passe oublié ?
                  </button>
                </div>

              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="reg-name" className="label-overline">Nom</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                        <Input
                          id="reg-name"
                          placeholder="Votre nom"
                          value={regName}
                          onChange={(e) => setRegName(e.target.value)}
                          className="pl-10 rounded-sm"
                          required
                          data-testid="register-name-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="reg-company" className="label-overline">Laboratoire</Label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                        <Input
                          id="reg-company"
                          placeholder="Nom du laboratoire"
                          value={regCompany}
                          onChange={(e) => setRegCompany(e.target.value)}
                          className="pl-10 rounded-sm"
                          data-testid="register-company-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-email" className="label-overline">Email</Label>
                    <div className="relative">
                      <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                      <Input
                        id="reg-email"
                        type="email"
                        placeholder="votre@email.com"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        className="pl-10 rounded-sm"
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-password" className="label-overline">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                      <Input
                        id="reg-password"
                        type={showRegPass ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        className="pl-10 pr-10 rounded-sm"
                        required
                        minLength={6}
                        data-testid="register-password-input"
                      />
                      <button type="button" onClick={() => setShowRegPass(!showRegPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors" data-testid="toggle-register-password">
                        {showRegPass ? <EyeSlash size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-phone" className="label-overline">Téléphone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                      <Input
                        id="reg-phone"
                        type="tel"
                        placeholder="+33 1 23 45 67 89"
                        value={regPhone}
                        onChange={(e) => setRegPhone(e.target.value)}
                        className="pl-10 rounded-sm"
                        data-testid="register-phone-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-address" className="label-overline">Adresse</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 text-zinc-400" size={18} />
                      <textarea
                        id="reg-address"
                        placeholder="Adresse complète"
                        value={regAddress}
                        onChange={(e) => setRegAddress(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-zinc-200 rounded-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-zinc-900"
                        data-testid="register-address-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full rounded-sm bg-zinc-900 hover:bg-zinc-800"
                    disabled={isLoading}
                    data-testid="register-submit-btn"
                  >
                    {isLoading ? <span className="spinner" /> : 'Créer mon compte'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
